import PropTypes from 'prop-types';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { Divider, Image, Menu, Sidebar, Segment } from 'components/HeroSemantic';
import { logout } from 'state/modules/auth';
import usePush from 'hooks/router/usePush';
import { UserStatus } from 'components';
import { getMenu } from 'components/Navigation';

const MobileSidebar = ({ closeSidebar, visible }) => {
  const user = useSelector(state => state.auth.user);
  const admin = useSelector(state => state.auth.admin);
  const screenType = useSelector(state => state.client.screenType);
  const tenantId = useSelector(state => state.tenant.id);
  const { formatMessage } = useIntl();

  const dispatch = useDispatch();
  const { push } = usePush();

  const handleClick = url => {
    push(url);
    closeSidebar();
  };

  const handleLogout = () => {
    dispatch(logout());
  };

  const filterPanes = (panes, acls) => {
    const userAcls = acls || [];
    // Copy panes and filter the children
    const panesCopy = panes.map(pane => {
      const filteredChildren = pane.children ? filterPanes(pane.children, userAcls) : [];
      return { ...pane, children: filteredChildren };
    });
    // Then filter the main panes & return all panes that are accessible by user's ACL
    return panesCopy
      .filter(pane => !pane.acl || pane.acl.some(level => userAcls.includes(level)))
      .filter(pane => !pane.view || pane.view.some(v => v.includes(screenType)));
  };

  const panes = getMenu(formatMessage, user, admin, tenantId);
  const userPanes = filterPanes(panes, user && user.acl);

  const renderChildren = child => (
    <Menu.Item
      key={`children-${child.title}`}
      onClick={() => handleClick(child.url)}
      style={{ fontSize: '14px', fontWeight: 600, lineHeight: '20px', paddingLeft: '48px', color: 'rgba(255,255,255,0.78)' }}
    >
      {child.title}
    </Menu.Item>
  );

  const renderMenu = pane => {
    const sectionLabelStyle = {
      color: 'rgba(255,255,255,0.55)',
      fontSize: '13px',
      fontWeight: 600,
      letterSpacing: '0.06em',
      lineHeight: '20px',
      paddingLeft: '28px',
      textTransform: 'uppercase',
    };

    // Standalone item with no children — render as clickable section-style label
    if (pane.children.length === 0) {
      return (
        <Menu.Item
          key={pane.title}
          onClick={() => handleClick(pane.url)}
          style={sectionLabelStyle}
        >
          {pane.title}
        </Menu.Item>
      );
    }

    // Single-child pane — render as a direct link
    if (pane.children.length === 1) {
      return (
        <Menu.Item
          key={pane.title}
          onClick={() => handleClick(pane.url)}
          style={{ fontSize: '15px', fontWeight: 700, lineHeight: '22px', paddingLeft: '28px', color: '#FFFFFF' }}
        >
          {pane.title}
        </Menu.Item>
      );
    }

    // Multi-child pane — render as non-clickable section header with children
    const menu = [
      <Menu.Item key={pane.title} style={{ ...sectionLabelStyle, cursor: 'default' }}>
        {pane.title}
      </Menu.Item>,
    ];
    pane.children.forEach(sub => menu.push(renderChildren(sub)));
    return menu;
  };

  return (
    <Sidebar as={Segment} animation="overlay" visible={visible} icon="labeled" vertical inverted style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={{ overflowY: 'auto', flex: 1 }}>
        <Menu secondary vertical inverted style={{ width: '100%', padding: '12px 0' }}>
          <Image src="/logo.png" alt="logo" style={{ maxWidth: '180px', maxHeight: '90px', margin: '8px 28px 12px', objectFit: 'contain' }} />
          { user && <Divider /> }
          {
            user && userPanes.map(pane => renderMenu(pane))
          }
        </Menu>
      </div>
      { user && <Divider /> }
      { user && <UserStatus float="left" inverted openUpward name={user.username} logout={() => handleLogout()} closeSidebar={closeSidebar} /> }
    </Sidebar>
  );
};

MobileSidebar.propTypes = {
  closeSidebar: PropTypes.func.isRequired,
  visible: PropTypes.bool.isRequired,
};

export default MobileSidebar;
